/**
 * AutoTradePanel
 * Performance dashboard for auto-trading with signals.
 * Tabs: OVERVIEW | TRADES | OPTIMIZER | SETTINGS
 */
import { memo, useState, useMemo, useCallback } from 'react';
import { useTerminalStore } from '../../stores/terminal.store';
import type { AutoTradeRecord, Candle, TimeframeKey } from '@arc/shared';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt$(n: number | undefined, decimals = 2): string {
  if (n === undefined || isNaN(n)) return '—';
  const abs = Math.abs(n);
  const s   = abs >= 1000 ? abs.toLocaleString('en-US', { maximumFractionDigits: 0 })
             : abs.toFixed(decimals);
  return (n < 0 ? '−$' : '+$') + s;
}

function fmtPct(n: number | undefined): string {
  if (n === undefined || isNaN(n)) return '—';
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
}

function fmtDuration(ms: number): string {
  if (ms < 60_000)   return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h`;
  return `${Math.round(ms / 86_400_000)}d`;
}

function pnlColor(n: number | undefined): string {
  if (n === undefined) return 'var(--color-muted)';
  return n >= 0 ? 'var(--color-buy)' : 'var(--color-sell)';
}

// ─── Performance Stats ────────────────────────────────────────────────────────

interface Stats {
  total: number;
  wins: number;
  losses: number;
  winRate: number;
  totalPnl: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  bestTrade: number;
  worstTrade: number;
  avgHoldingMs: number;
}

function calcStats(closed: AutoTradeRecord[]): Stats {
  const total    = closed.length;
  const wins     = closed.filter(r => (r.pnl ?? 0) >= 0);
  const losses   = closed.filter(r => (r.pnl ?? 0) < 0);
  const winPnl   = wins.reduce((s, r) => s + (r.pnl ?? 0), 0);
  const lossPnl  = Math.abs(losses.reduce((s, r) => s + (r.pnl ?? 0), 0));
  const allPnl   = closed.map(r => r.pnl ?? 0);
  return {
    total,
    wins:         wins.length,
    losses:       losses.length,
    winRate:      total ? (wins.length / total) * 100 : 0,
    totalPnl:     allPnl.reduce((a, b) => a + b, 0),
    avgWin:       wins.length ? winPnl / wins.length : 0,
    avgLoss:      losses.length ? -lossPnl / losses.length : 0,
    profitFactor: lossPnl > 0 ? winPnl / lossPnl : wins.length > 0 ? Infinity : 0,
    bestTrade:    allPnl.length ? Math.max(...allPnl) : 0,
    worstTrade:   allPnl.length ? Math.min(...allPnl) : 0,
    avgHoldingMs: total ? closed.reduce((s, r) => s + (r.holdingMs ?? 0), 0) / total : 0,
  };
}

// ─── Equity Curve SVG ────────────────────────────────────────────────────────

function EquityCurve({ height = 80 }: { height?: number }) {
  const equityCurve = useTerminalStore(s => s.equityCurve);
  if (equityCurve.length < 2) {
    return (
      <div className="flex items-center justify-center text-[11px] opacity-90"
           style={{ height, color: 'var(--color-muted)' }}>
        NO DATA YET
      </div>
    );
  }
  const equities   = equityCurve.map(p => p.equity);
  const minE = Math.min(...equities);
  const maxE = Math.max(...equities);
  const range = maxE - minE || 1;
  const W = 300; const H = height;
  const points = equityCurve.map((p, i) => {
    const x = (i / (equityCurve.length - 1)) * W;
    const y = H - ((p.equity - minE) / range) * (H - 6) - 3;
    return `${x},${y}`;
  }).join(' ');
  const lastEquity = equities.at(-1) ?? 10000;
  const firstEquity = equities[0] ?? 10000;
  const isUp = lastEquity >= firstEquity;
  const lineColor = isUp ? 'var(--color-buy)' : 'var(--color-sell)';

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.25" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Fill area */}
      <polygon
        points={`0,${H} ${points} ${W},${H}`}
        fill="url(#eqGrad)"
      />
      {/* Line */}
      <polyline points={points} fill="none" stroke={lineColor} strokeWidth="1.5" />
      {/* Start / end dots */}
      <circle cx={0} cy={H - ((equities[0]! - minE) / range) * (H - 6) - 3}
              r="2" fill={lineColor} />
      <circle cx={W} cy={H - ((equities.at(-1)! - minE) / range) * (H - 6) - 3}
              r="2.5" fill={lineColor} />
    </svg>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-[11px] tracking-widest opacity-90" style={{ color: 'var(--color-text)' }}>
        {label}
      </span>
      <span className="font-mono text-[11px] font-bold" style={{ color: color ?? 'var(--color-bright)' }}>
        {value}
      </span>
    </div>
  );
}

// ─── UT Bot backtest (pure) ───────────────────────────────────────────────────

interface BacktestResult {
  keyValue:     number;
  atrPeriod:    number;
  trades:       number;
  winRate:      number;
  totalPnl:     number;
  profitFactor: number;
}

function runBacktest(candles: Candle[], keyValue: number, atrPeriod: number, sizeUsd: number): BacktestResult {
  if (candles.length < atrPeriod + 5) return { keyValue, atrPeriod, trades: 0, winRate: 0, totalPnl: 0, profitFactor: 0 };

  // Wilder ATR
  const trs = candles.map((c, i) => {
    if (i === 0) return c.high - c.low;
    const prev = candles[i - 1]!.close;
    return Math.max(c.high - c.low, Math.abs(c.high - prev), Math.abs(c.low - prev));
  });
  const atrs = new Array(candles.length).fill(0);
  const seed = trs.slice(0, atrPeriod).reduce((a, b) => a + b, 0) / atrPeriod;
  atrs[atrPeriod - 1] = seed;
  for (let i = atrPeriod; i < candles.length; i++) {
    atrs[i] = (atrs[i - 1]! * (atrPeriod - 1) + trs[i]!) / atrPeriod;
  }

  // Trail stop
  let trail = 0;
  let position: { side: 'long' | 'short'; entry: number } | null = null;
  let wins = 0; let losses = 0; let grossWin = 0; let grossLoss = 0;

  for (let i = atrPeriod; i < candles.length; i++) {
    const src   = candles[i]!.close;
    const nLoss = keyValue * atrs[i]!;
    const prev  = candles[i - 1]!.close;
    const prevTrail = trail;

    if (src > prevTrail) trail = Math.max(prevTrail, src - nLoss);
    else                  trail = Math.min(prevTrail, src + nLoss);

    const crossedAbove = prev <= prevTrail && src > trail;
    const crossedBelow = prev >= prevTrail && src < trail;

    if (crossedAbove) {
      if (position?.side === 'short') {
        const pnl = (position.entry - src) / position.entry * sizeUsd;
        pnl >= 0 ? (wins++, grossWin += pnl) : (losses++, grossLoss += Math.abs(pnl));
        position = null;
      }
      position = { side: 'long', entry: src };
    }
    if (crossedBelow) {
      if (position?.side === 'long') {
        const pnl = (src - position.entry) / position.entry * sizeUsd;
        pnl >= 0 ? (wins++, grossWin += pnl) : (losses++, grossLoss += Math.abs(pnl));
        position = null;
      }
      position = { side: 'short', entry: src };
    }
  }

  const trades = wins + losses;
  return {
    keyValue, atrPeriod, trades,
    winRate:      trades ? (wins / trades) * 100 : 0,
    totalPnl:     grossWin - grossLoss,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : wins > 0 ? 999 : 0,
  };
}

// ─── Optimizer Tab ───────────────────────────────────────────────────────────

const KEY_VALUES  = [0.5, 1, 1.5, 2, 2.5, 3];
const ATR_PERIODS = [5, 7, 10, 14, 20];
const TIMEFRAMES: TimeframeKey[] = ['15m', '1h', '4h'];

function OptimizerTab() {
  const candles          = useTerminalStore(s => s.candles);
  const setAutoTradeConfig = useTerminalStore(s => s.setAutoTradeConfig);
  const setBotConfig     = useTerminalStore(s => s.setBotConfig);
  const autoTradeConfig  = useTerminalStore(s => s.autoTradeConfig);
  const [tf, setTf]      = useState<TimeframeKey>('1h');
  const [symbol, setSymbol] = useState('BTC');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<BacktestResult[]>([]);

  const symbols = useMemo(() => {
    return [...new Set(Object.keys(candles).map(k => k.split(':')[0]!))];
  }, [candles]);

  const runOptimizer = useCallback(() => {
    const key = `${symbol}:${tf}`;
    const data = candles[key] ?? [];
    if (data.length < 30) return;
    setRunning(true);
    setTimeout(() => {
      const res: BacktestResult[] = [];
      for (const kv of KEY_VALUES) {
        for (const ap of ATR_PERIODS) {
          res.push(runBacktest(data, kv, ap, 500));
        }
      }
      res.sort((a, b) => b.totalPnl - a.totalPnl);
      setResults(res);
      setRunning(false);
    }, 50);
  }, [candles, symbol, tf]);

  const applyBest = useCallback(() => {
    const best = results[0];
    if (!best) return;
    setAutoTradeConfig({ symbols: [symbol], timeframes: [tf] });
    // The bot trades with its own UT Bot settings, so apply the winner there.
    setBotConfig({ keyValue: best.keyValue, atrPeriod: best.atrPeriod });
  }, [results, symbol, tf, setAutoTradeConfig, setBotConfig]);

  return (
    <div className="flex flex-col gap-3 p-2 overflow-y-auto h-full" style={{ fontSize: 11 }}>
      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={symbol} onChange={e => setSymbol(e.target.value)}
          className="font-mono text-[11px] px-2 py-1 border"
          style={{ background: 'var(--color-panel)', borderColor: 'var(--color-border)', color: 'var(--color-text)' }}>
          {symbols.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex">
          {TIMEFRAMES.map(t => (
            <button key={t} onClick={() => setTf(t)}
              className="font-mono text-[11px] px-2 py-1 border-y border-r first:border-l transition-all"
              style={{
                borderColor: 'var(--color-border)',
                background:  tf === t ? 'var(--color-glow)' : 'transparent',
                color:       tf === t ? 'var(--color-arc)'  : 'var(--color-muted)',
              }}>
              {t}
            </button>
          ))}
        </div>
        <button onClick={runOptimizer} disabled={running}
          className="font-mono text-[11px] px-3 py-1 border tracking-widest transition-all"
          style={{ borderColor: 'var(--color-arc)', color: 'var(--color-arc)', background: 'var(--color-glow)' }}>
          {running ? 'RUNNING...' : '⚡ RUN OPTIMIZER'}
        </button>
        {results.length > 0 && (
          <button onClick={applyBest}
            className="font-mono text-[11px] px-3 py-1 border tracking-widest"
            style={{ borderColor: 'var(--color-buy)', color: 'var(--color-buy)' }}>
            ✓ APPLY BEST
          </button>
        )}
      </div>

      {/* Description */}
      <p className="font-mono text-[11px] opacity-90" style={{ color: 'var(--color-text)' }}>
        Tests {KEY_VALUES.length}×{ATR_PERIODS.length} = {KEY_VALUES.length * ATR_PERIODS.length} parameter combos on {symbol} {tf} candles.
        Each trade: ${autoTradeConfig.sizeUsd} LONG-only, signal close exit.
      </p>

      {/* Results table */}
      {results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full font-mono text-[11px]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)' }}>
                {['RANK','KEY','ATR','TRADES','WIN%','TOTAL PNL','PF'].map(h => (
                  <th key={h} className="text-left py-1 px-1.5 tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.slice(0, 15).map((r, i) => (
                <tr key={`${r.keyValue}-${r.atrPeriod}`}
                    style={{ borderBottom: '1px solid var(--color-border)', opacity: i === 0 ? 1 : 0.7 }}>
                  <td className="py-1 px-1.5" style={{ color: i === 0 ? 'var(--color-arc)' : 'var(--color-muted)' }}>
                    {i === 0 ? '★' : `#${i + 1}`}
                  </td>
                  <td className="py-1 px-1.5" style={{ color: 'var(--color-bright)' }}>{r.keyValue}</td>
                  <td className="py-1 px-1.5" style={{ color: 'var(--color-bright)' }}>{r.atrPeriod}</td>
                  <td className="py-1 px-1.5" style={{ color: 'var(--color-text)' }}>{r.trades}</td>
                  <td className="py-1 px-1.5" style={{ color: r.winRate >= 50 ? 'var(--color-buy)' : 'var(--color-sell)' }}>
                    {r.winRate.toFixed(0)}%
                  </td>
                  <td className="py-1 px-1.5" style={{ color: pnlColor(r.totalPnl) }}>
                    {fmt$(r.totalPnl)}
                  </td>
                  <td className="py-1 px-1.5" style={{ color: r.profitFactor >= 1 ? 'var(--color-buy)' : 'var(--color-sell)' }}>
                    {r.profitFactor === 999 ? '∞' : r.profitFactor.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {results.length === 0 && !running && (
        <div className="font-mono text-[11px] opacity-90 text-center py-4" style={{ color: 'var(--color-text)' }}>
          Load candles on a chart first, then run the optimizer
        </div>
      )}
    </div>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────

function SettingsTab() {
  const cfg    = useTerminalStore(s => s.autoTradeConfig);
  const setCfg = useTerminalStore(s => s.setAutoTradeConfig);
  const reset  = useTerminalStore(s => s.resetAutoTrade);
  const tokens = useTerminalStore(s => s.tokens);

  const toggleSymbol = (sym: string) => {
    const next = cfg.symbols.includes(sym)
      ? cfg.symbols.filter(s => s !== sym)
      : [...cfg.symbols, sym];
    setCfg({ symbols: next });
  };

  const TIMEFRAMES: TimeframeKey[] = ['15m', '1h', '4h', '1D'];
  const toggleTf = (tf: TimeframeKey) => {
    const next = cfg.timeframes.includes(tf)
      ? cfg.timeframes.filter(t => t !== tf)
      : [...cfg.timeframes, tf];
    setCfg({ timeframes: next });
  };

  return (
    <div className="flex flex-col gap-4 p-2 overflow-y-auto h-full font-mono text-[11px]">
      {/* Master toggle */}
      <div className="flex items-center justify-between p-2 border"
           style={{ borderColor: cfg.enabled ? 'var(--color-arc)' : 'var(--color-border)',
                    background: cfg.enabled ? 'var(--color-glow)' : 'transparent' }}>
        <div>
          <div className="text-[11px] font-bold tracking-widest" style={{ color: cfg.enabled ? 'var(--color-arc)' : 'var(--color-bright)' }}>
            {cfg.enabled ? '⚡ AUTO-TRADE ACTIVE' : '○ AUTO-TRADE OFF'}
          </div>
          <div className="text-[11px] opacity-90 mt-0.5" style={{ color: 'var(--color-text)' }}>
            Executes paper trades on incoming UT Bot signals
          </div>
        </div>
        <button onClick={() => setCfg({ enabled: !cfg.enabled })}
          className="px-4 py-1.5 border tracking-widest text-[11px] transition-all"
          style={{
            borderColor: cfg.enabled ? 'var(--color-sell)' : 'var(--color-buy)',
            color:       cfg.enabled ? 'var(--color-sell)' : 'var(--color-buy)',
            background:  'transparent',
          }}>
          {cfg.enabled ? 'DISABLE' : 'ENABLE'}
        </button>
      </div>

      {/* Mode */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] tracking-widest opacity-90" style={{ color: 'var(--color-text)' }}>TRADE MODE</label>
        <div className="flex">
          {(['long_only', 'both', 'short_only'] as const).map(m => (
            <button key={m} onClick={() => setCfg({ mode: m })}
              className="flex-1 py-1 border-y border-r first:border-l text-[11px] tracking-widest transition-all"
              style={{
                borderColor: 'var(--color-border)',
                background:  cfg.mode === m ? 'var(--color-glow)' : 'transparent',
                color:       cfg.mode === m ? 'var(--color-arc)'  : 'var(--color-muted)',
              }}>
              {m === 'long_only' ? 'LONG' : m === 'short_only' ? 'SHORT' : 'BOTH'}
            </button>
          ))}
        </div>
      </div>

      {/* Position size */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] tracking-widest opacity-90" style={{ color: 'var(--color-text)' }}>
          SIZE PER TRADE — ${cfg.sizeUsd}
        </label>
        <input type="range" min={100} max={2000} step={100}
          value={cfg.sizeUsd}
          onChange={e => setCfg({ sizeUsd: Number(e.target.value) })}
          className="w-full h-1 appearance-none rounded"
          style={{ accentColor: 'var(--color-arc)' }}
        />
        <div className="flex justify-between text-[11px] opacity-90" style={{ color: 'var(--color-muted)' }}>
          <span>$100</span><span>$500</span><span>$1000</span><span>$2000</span>
        </div>
      </div>

      {/* Leverage */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] tracking-widest opacity-90" style={{ color: 'var(--color-text)' }}>
          LEVERAGE — {cfg.leverage}×
        </label>
        <input type="range" min={1} max={10} step={1}
          value={cfg.leverage}
          onChange={e => setCfg({ leverage: Number(e.target.value) })}
          className="w-full h-1 appearance-none rounded"
          style={{ accentColor: 'var(--color-arc)' }}
        />
      </div>

      {/* Max positions */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] tracking-widest opacity-90" style={{ color: 'var(--color-text)' }}>
          MAX OPEN POSITIONS — {cfg.maxPositions}
        </label>
        <input type="range" min={1} max={10} step={1}
          value={cfg.maxPositions}
          onChange={e => setCfg({ maxPositions: Number(e.target.value) })}
          className="w-full h-1 appearance-none rounded"
          style={{ accentColor: 'var(--color-arc)' }}
        />
      </div>

      {/* Symbol filter */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] tracking-widest opacity-90" style={{ color: 'var(--color-text)' }}>
          SYMBOLS {cfg.symbols.length === 0 ? '(ALL)' : `(${cfg.symbols.join(', ')})`}
        </label>
        <div className="flex flex-wrap gap-1">
          {tokens.map(t => (
            <button key={t.symbol} onClick={() => toggleSymbol(t.symbol)}
              className="px-2 py-0.5 border text-[11px] tracking-wide transition-all"
              style={{
                borderColor: cfg.symbols.includes(t.symbol) ? 'var(--color-arc)' : 'var(--color-border)',
                color:       cfg.symbols.includes(t.symbol) ? 'var(--color-arc)' : 'var(--color-muted)',
                background:  cfg.symbols.includes(t.symbol) ? 'var(--color-glow)' : 'transparent',
              }}>
              {t.symbol}
            </button>
          ))}
        </div>
        {cfg.symbols.length > 0 && (
          <button onClick={() => setCfg({ symbols: [] })} className="text-[11px] text-left opacity-40 hover:opacity-70" style={{ color: 'var(--color-text)' }}>
            Clear (trade all symbols)
          </button>
        )}
      </div>

      {/* Timeframe filter */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] tracking-widest opacity-90" style={{ color: 'var(--color-text)' }}>
          TIMEFRAMES {cfg.timeframes.length === 0 ? '(ALL)' : `(${cfg.timeframes.join(', ')})`}
        </label>
        <div className="flex gap-1">
          {TIMEFRAMES.map(tf => (
            <button key={tf} onClick={() => toggleTf(tf)}
              className="px-2 py-0.5 border text-[11px] transition-all"
              style={{
                borderColor: cfg.timeframes.includes(tf) ? 'var(--color-arc)' : 'var(--color-border)',
                color:       cfg.timeframes.includes(tf) ? 'var(--color-arc)' : 'var(--color-muted)',
                background:  cfg.timeframes.includes(tf) ? 'var(--color-glow)' : 'transparent',
              }}>
              {tf}
            </button>
          ))}
        </div>
      </div>

      {/* Reset */}
      <button onClick={() => { if (confirm('Reset all auto-trade records and equity curve?')) reset(); }}
        className="border py-1.5 text-[11px] tracking-widest transition-all mt-2"
        style={{ borderColor: 'var(--color-sell)', color: 'var(--color-sell)', background: 'transparent' }}>
        ✕ RESET AUTO-TRADE HISTORY
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const AutoTradePanel = memo(function AutoTradePanel() {
  const [tab, setTab] = useState<'overview' | 'trades' | 'optimizer' | 'settings'>('overview');
  const autoTradeRecords = useTerminalStore(s => s.autoTradeRecords);
  const autoTradeConfig  = useTerminalStore(s => s.autoTradeConfig);
  const equityCurve      = useTerminalStore(s => s.equityCurve);
  const prices           = useTerminalStore(s => s.prices);

  const closed = useMemo(() => autoTradeRecords.filter(r => r.status === 'closed'), [autoTradeRecords]);
  const open   = useMemo(() => autoTradeRecords.filter(r => r.status === 'open'),   [autoTradeRecords]);
  const stats  = useMemo(() => calcStats(closed), [closed]);

  const startEquity = equityCurve[0]?.equity ?? 10000;
  const curEquity   = equityCurve.at(-1)?.equity ?? startEquity;
  const totalReturn = ((curEquity - startEquity) / startEquity) * 100;

  const TABS = [
    { id: 'overview',  label: 'OVERVIEW' },
    { id: 'trades',    label: `TRADES${autoTradeRecords.length > 0 ? ` (${autoTradeRecords.length})` : ''}` },
    { id: 'optimizer', label: 'OPTIMIZER' },
    { id: 'settings',  label: 'SETTINGS' },
  ] as const;

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ color: 'var(--color-text)', fontSize: 11 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1 border-b flex-shrink-0"
           style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] tracking-widest" style={{ color: 'var(--color-arc)' }}>
            ⚡ AUTO-TRADE
          </span>
          <span className="font-mono text-[11px] px-1.5 py-0.5 border"
            style={{
              borderColor: autoTradeConfig.enabled ? 'var(--color-buy)' : 'var(--color-border)',
              color:       autoTradeConfig.enabled ? 'var(--color-buy)' : 'var(--color-muted)',
              background:  autoTradeConfig.enabled ? 'rgba(0,255,136,0.1)' : 'transparent',
            }}>
            {autoTradeConfig.enabled ? 'LIVE' : 'OFF'}
          </span>
          {open.length > 0 && (
            <span className="font-mono text-[11px] px-1.5 py-0.5 border animate-pulse"
              style={{ borderColor: 'var(--color-arc)', color: 'var(--color-arc)', background: 'var(--color-glow)' }}>
              {open.length} OPEN
            </span>
          )}
        </div>
        <span className="font-mono text-[11px] font-bold" style={{ color: pnlColor(curEquity - startEquity) }}>
          {fmtPct(totalReturn)}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap border-b flex-shrink-0" style={{ borderColor: 'var(--color-border)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="flex-1 min-w-fit whitespace-nowrap px-2 font-mono text-[11px] py-1.5 tracking-widest transition-all"
            style={{
              borderBottom: tab === t.id ? `1px solid var(--color-arc)` : '1px solid transparent',
              color:        tab === t.id ? 'var(--color-arc)' : 'var(--color-bright)',
              background:   tab === t.id ? 'var(--color-glow)' : 'transparent',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <div className="flex flex-col gap-3 p-2 overflow-y-auto h-full">
            {/* Equity curve */}
            <div className="border p-2" style={{ borderColor: 'var(--color-border)' }}>
              <div className="font-mono text-[11px] tracking-widest opacity-90 mb-1" style={{ color: 'var(--color-text)' }}>
                EQUITY CURVE
              </div>
              <EquityCurve height={70} />
              <div className="flex justify-between font-mono text-[11px] mt-1" style={{ color: 'var(--color-muted)' }}>
                <span>${startEquity.toLocaleString()}</span>
                <span style={{ color: pnlColor(curEquity - startEquity) }}>
                  ${curEquity.toLocaleString('en-US', { maximumFractionDigits: 0 })} ({fmtPct(totalReturn)})
                </span>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-2 border p-2" style={{ borderColor: 'var(--color-border)' }}>
              <Stat label="TRADES"   value={String(stats.total)} />
              <Stat label="WIN RATE" value={stats.total ? stats.winRate.toFixed(0) + '%' : '—'}
                    color={stats.winRate >= 50 ? 'var(--color-buy)' : 'var(--color-sell)'} />
              <Stat label="TOTAL P&L" value={fmt$(stats.totalPnl)} color={pnlColor(stats.totalPnl)} />
              <Stat label="AVG WIN"   value={fmt$(stats.avgWin)}  color="var(--color-buy)" />
              <Stat label="AVG LOSS"  value={fmt$(stats.avgLoss)} color="var(--color-sell)" />
              <Stat label="PROFIT FACTOR"
                    value={stats.profitFactor === Infinity ? '∞' : stats.profitFactor.toFixed(2)}
                    color={stats.profitFactor >= 1 ? 'var(--color-buy)' : 'var(--color-sell)'} />
              <Stat label="BEST TRADE"  value={fmt$(stats.bestTrade)}  color="var(--color-buy)" />
              <Stat label="WORST TRADE" value={fmt$(stats.worstTrade)} color="var(--color-sell)" />
              <Stat label="AVG HOLD"    value={stats.avgHoldingMs ? fmtDuration(stats.avgHoldingMs) : '—'} />
            </div>

            {/* By symbol breakdown */}
            {stats.total > 0 && (() => {
              const bySymbol: Record<string, { total: number; wins: number; pnl: number }> = {};
              closed.forEach(r => {
                if (!bySymbol[r.symbol]) bySymbol[r.symbol] = { total: 0, wins: 0, pnl: 0 };
                bySymbol[r.symbol]!.total++;
                if ((r.pnl ?? 0) >= 0) bySymbol[r.symbol]!.wins++;
                bySymbol[r.symbol]!.pnl += r.pnl ?? 0;
              });
              return (
                <div className="border" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="font-mono text-[11px] tracking-widest opacity-90 px-2 pt-1.5" style={{ color: 'var(--color-text)' }}>BY SYMBOL</div>
                  {Object.entries(bySymbol).map(([sym, d]) => (
                    <div key={sym} className="flex items-center justify-between px-2 py-1 border-t"
                         style={{ borderColor: 'var(--color-border)' }}>
                      <span className="font-mono text-[11px] font-bold" style={{ color: 'var(--color-bright)' }}>{sym}</span>
                      <span className="font-mono text-[11px]" style={{ color: 'var(--color-muted)' }}>{d.total} trades · {Math.round(d.wins / d.total * 100)}% win</span>
                      <span className="font-mono text-[11px] font-bold" style={{ color: pnlColor(d.pnl) }}>{fmt$(d.pnl)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {stats.total === 0 && (
              <div className="font-mono text-[11px] text-center py-6 opacity-90" style={{ color: 'var(--color-text)' }}>
                {autoTradeConfig.enabled
                  ? 'Waiting for signals to trade...'
                  : 'Enable auto-trade in SETTINGS to start'}
              </div>
            )}
          </div>
        )}

        {/* ── TRADES ── */}
        {tab === 'trades' && (
          <div className="overflow-y-auto h-full">
            {autoTradeRecords.length === 0 ? (
              <div className="font-mono text-[11px] text-center py-8 opacity-90" style={{ color: 'var(--color-text)' }}>
                No trades yet
              </div>
            ) : (
              <table className="w-full font-mono text-[11px]" style={{ borderCollapse: 'collapse' }}>
                <thead className="sticky top-0" style={{ background: 'var(--color-panel)' }}>
                  <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)' }}>
                    {['SYM','SIDE','TF','ENTRY','EXIT','P&L','STATUS'].map(h => (
                      <th key={h} className="text-left py-1 px-1.5 tracking-widest text-[11px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {autoTradeRecords.map(r => {
                    // Live unrealized P&L for open trades; realized P&L for closed
                    const livePrice = r.status === 'open' ? (prices[r.symbol]?.priceUsd ?? r.entryPrice) : undefined;
                    const displayPnl = r.status === 'open' && livePrice !== undefined
                      ? (r.side === 'buy'
                          ? (livePrice - r.entryPrice) / r.entryPrice * r.sizeUsd
                          : (r.entryPrice - livePrice) / r.entryPrice * r.sizeUsd)
                      : r.pnl;
                    const displayExit = r.status === 'open' ? livePrice : r.exitPrice;
                    return (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td className="py-1 px-1.5 font-bold" style={{ color: 'var(--color-bright)' }}>{r.symbol}</td>
                      <td className="py-1 px-1.5" style={{ color: r.side === 'buy' ? 'var(--color-buy)' : 'var(--color-sell)' }}>
                        {r.side === 'buy' ? '▲ LONG' : '▼ SHORT'}
                      </td>
                      <td className="py-1 px-1.5" style={{ color: 'var(--color-muted)' }}>{r.timeframe}</td>
                      <td className="py-1 px-1.5" style={{ color: 'var(--color-text)' }}>${r.entryPrice.toLocaleString('en-US', { maximumFractionDigits: 2 })}</td>
                      <td className="py-1 px-1.5" style={{ color: r.status === 'open' ? 'var(--color-arc)' : 'var(--color-text)' }}>
                        {displayExit ? `$${displayExit.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td className="py-1 px-1.5 font-bold" style={{ color: pnlColor(displayPnl) }}>
                        {displayPnl !== undefined ? fmt$(displayPnl) : '—'}
                        {r.status === 'open' && <span className="opacity-90 text-[11px] ml-0.5">~</span>}
                      </td>
                      <td className="py-1 px-1.5">
                        <span className="px-1.5 py-0.5 text-[11px] tracking-widest"
                          style={{
                            background:  r.status === 'open' ? 'var(--color-glow)' : 'transparent',
                            border:      `1px solid ${r.status === 'open' ? 'var(--color-arc)' : 'var(--color-border)'}`,
                            color:       r.status === 'open' ? 'var(--color-arc)' : 'var(--color-muted)',
                          }}>
                          {r.status.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── OPTIMIZER ── */}
        {tab === 'optimizer' && <OptimizerTab />}

        {/* ── SETTINGS ── */}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
});
